import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Truck, Shield, Leaf } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchCategories();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=4`);
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>OrgShop - Buy Wood Pressed Marachekku Oils Online | Groundnut, Sesame & Coconut Oil in Tamil Nadu</title>
        <meta 
          name="description" 
          content="Buy authentic wood-pressed (Marachekku) oils online from OrgShop. Shop pure Groundnut Oil(kadalennai), Sesame Oil (Nallennai), and Coconut Oil (Thengai Ennai) sourced from trusted farmers. Cold pressed, unrefined, and chemical-free." 
        />
        <meta 
          name="keywords" 
          content="Groundnut Oil, Sesame Oil, Coconut Oil, Nallennai, Kadalennai, Thengaennai, Maracheckku ennai, Cold pressed oil, Marachekku Oil, Kadalai ennai, nalla ennai, thengai ennai" 
        />
        <meta property="og:title" content="OrgShop - Buy Wood Pressed Marachekku Oils Online | Tamil Nadu" />
        <meta property="og:description" content="Buy authentic wood-pressed (Marachekku) oils online. Pure Groundnut, Sesame & Coconut Oil from trusted farmers." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[360px] md:min-h-[420px] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=1600&q=80"
            alt="Wood pressed cooking oils"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/55" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-xl">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary-foreground mb-4 leading-tight" data-testid="hero-title">
              Pure Wood-Pressed Cooking Oils
            </h1>
            <p className="text-base md:text-lg text-primary-foreground/90 mb-6 leading-relaxed">
              Authentic Marachekku oils — Groundnut, Sesame & Coconut — cold-pressed, unrefined and chemical-free, delivered from trusted Tamil Nadu farmers.
            </p>
            <Link to="/products">
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-7 py-5 text-base md:text-lg font-medium shadow-soft" data-testid="hero-cta-btn">
                Shop Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 md:py-20" data-testid="featured-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">Products</h2>
              <p className="text-lg text-muted-foreground">Handpicked organic essentials for you</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/products">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg" data-testid="view-all-products-btn">
                  View All Products
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <section className="py-16 md:py-20 bg-background-alt" data-testid="categories-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">Shop by Category</h2>
              <p className="text-lg text-muted-foreground">Explore our range of organic products</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {categories.map((category) => (
                <Link key={category.id} to={`/products?category=${category.id}`} data-testid="category-card">
                  <div className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1 text-center">
                    {category.image && (
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden bg-muted">
                        <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <h3 className="font-heading text-lg font-semibold text-foreground">{category.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Highlights Section */}
      <section className="py-16 md:py-20 bg-background" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">Why Choose OrgShop</h2>
            <p className="text-lg text-muted-foreground">Quality and trust in every order</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card rounded-2xl p-8 shadow-soft text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">100% Organic</h3>
              <p className="text-muted-foreground">Certified organic products from trusted vendors</p>
            </div>
            <div className="bg-card rounded-2xl p-8 shadow-soft text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">Fast Delivery</h3>
              <p className="text-muted-foreground">Direct shipping from vendors nationwide</p>
            </div>
            <div className="bg-card rounded-2xl p-8 shadow-soft text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">Secure Shopping</h3>
              <p className="text-muted-foreground">Safe payments with Razorpay integration</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">Become a Vendor</h2>
          <p className="text-lg text-primary-foreground/90 mb-8">
            Join our marketplace and reach thousands of customers looking for quality organic products.
          </p>
          <Link to="/become-vendor">
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium" data-testid="become-vendor-btn">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
